import { deDasherize, humanize } from '@zoocasa/node-kit/strings';
import { Filter, Sort } from 'contexts/preferences/listing-params';
import { PartialDeep } from 'type-fest';
import { getPageFromURLSearchParams, getSortFromURLSearchParams } from 'utils/listing-query-helper';
import { MilitaryBranches } from '@zoocasa/go-search';
import { MilitaryBranchType } from './types';
import {
  createFilterFromFilterParams,
  createFilterFromSeoFilter,
  HomeTypeFilter, isFilterSegment,
  ListingStatusFilter, parseSegmentForSearchParams, parseSeoFilter,
  PropertyFeatureFilter,
} from 'data/addresses/go-search/addressPathParser';
import { AddressType } from '@zoocasa/go-search';

export const MILITARY_BRANCH_ARMY_SUFFIX = 'army';
export const MILITARY_BRANCH_NAVY_SUFFIX = 'navy';
export const MILITARY_BRANCH_AIR_FORCE_SUFFIX = 'air-force';
export const MILITARY_BRANCH_MARINE_CORPS_SUFFIX = 'marine-corps';
export const MILITARY_BRANCH_COAST_GUARD_SUFFIX = 'coast-guard';

export type MilitaryBranchSuffix = typeof MILITARY_BRANCH_ARMY_SUFFIX | typeof MILITARY_BRANCH_NAVY_SUFFIX | typeof MILITARY_BRANCH_AIR_FORCE_SUFFIX | typeof MILITARY_BRANCH_MARINE_CORPS_SUFFIX | typeof MILITARY_BRANCH_COAST_GUARD_SUFFIX;

export type SeoFilters = {
  listingStatus?: ListingStatusFilter;
  homeType?: HomeTypeFilter;
  propertyFeature?: PropertyFeatureFilter;
};

/**
 * Represents the parsed components of an military base URL path
 */
export type ParsedMilitaryPath = {
  type: AddressType;
  branch: MilitaryBranchType;
  baseName: string;
  acceptedSegments?: string[];
  discardedSegments?: string[];
  acceptedPathSegments?: string[];
  acceptedSeoFilterSegment?: string;
  sort?: Sort;
  page?: number;
  slug?: string;
  filter?: PartialDeep<Filter>;
  seoFilters?: SeoFilters;
};
//#endregion

//#region constants
export const MILITARY_BRANCH_SUFFIXES: MilitaryBranchSuffix[] = [MILITARY_BRANCH_ARMY_SUFFIX, MILITARY_BRANCH_NAVY_SUFFIX, MILITARY_BRANCH_AIR_FORCE_SUFFIX, MILITARY_BRANCH_MARINE_CORPS_SUFFIX, MILITARY_BRANCH_COAST_GUARD_SUFFIX];

export const MILITARY_BRANCH_SUFFIX_TO_BRANCH_MAP: Record<MilitaryBranchSuffix, MilitaryBranchType> = {
  [MILITARY_BRANCH_ARMY_SUFFIX]: MilitaryBranches.Army,
  [MILITARY_BRANCH_NAVY_SUFFIX]: MilitaryBranches.Navy,
  [MILITARY_BRANCH_AIR_FORCE_SUFFIX]: MilitaryBranches.AirForce,
  [MILITARY_BRANCH_MARINE_CORPS_SUFFIX]: MilitaryBranches.MarineCorps,
  [MILITARY_BRANCH_COAST_GUARD_SUFFIX]: MilitaryBranches.CoastGuard,
};
//#endregion

//#region exported functions

/**
 * Parses a military base URL path into a structured ParsedMilitaryPath object.
 * This function handles various URL formats including SEO filters, query parameters, and filter segments.
 *
 * @param path - The URL path to parse. Can include military base name, branch suffix, SEO filters, and query parameters.
 *               Examples:
 *               - "naval-air-station-whiting-field-navy"
 *               - "naval-air-station-whiting-field-navy/for-rent"
 *               - "naval-air-station-whiting-field-navy?page=2&sort=date"
 *               - "naval-air-station-whiting-field-navy/filter?bedrooms=2"
 *
 * @returns A ParsedMilitaryPath object containing:
 *          - branch: The military branch (Army, Navy, AirForce, etc.)
 *          - baseName: Human-readable name of the military base
 *          - slug: URL-friendly version of the base name
 *          - acceptedSegments: Array of valid path segments
 *          - acceptedPathSegments: Array of base path segments
 *          - acceptedSeoFilterSegment: SEO filter segment if present
 *          - discardedSegments: Array of unused path segments
 *          - sort: Sort parameter if present
 *          - page: Page number if present
 *          - filter: Parsed filter parameters if present
 *          - seoFilters: Parsed SEO filters if present
 *
 * @example
 * // Basic military base path
 * parseMilitaryPath("naval-air-station-whiting-field-navy")
 * // Returns: {
 * //   branch: MilitaryBranches.Navy,
 * //   baseName: "Naval Air Station Whiting Field",
 * //   slug: "naval-air-station-whiting-field",
 * //   ...
 * // }
 *
 * @example
 * // Path with SEO filter
 * parseMilitaryPath("naval-air-station-whiting-field-navy/for-rent")
 * // Returns: {
 * //   branch: MilitaryBranches.Navy,
 * //   baseName: "Naval Air Station Whiting Field",
 * //   seoFilters: { listingStatus: "for-rent" },
 * //   ...
 * // }
 *
 * @example
 * // Path with query parameters
 * parseMilitaryPath("naval-air-station-whiting-field-navy?page=2&sort=date")
 * // Returns: {
 * //   branch: MilitaryBranches.Navy,
 * //   baseName: "Naval Air Station Whiting Field",
 * //   page: 2,
 * //   sort: "date",
 * //   ...
 * // }
 *
 * @example
 * // Path with filter segment
 * parseMilitaryPath("naval-air-station-whiting-field-navy/filter?bedrooms=2")
 * // Returns: {
 * //   branch: MilitaryBranches.Navy,
 * //   baseName: "Naval Air Station Whiting Field",
 * //   filter: { bedrooms: "2" },
 * //   ...
 * // }
 */
export function parseMilitaryPath(path: string): ParsedMilitaryPath {
  // Remove leading/trailing slashes and split into segments
  const cleanPath = path.replace(/^\/|\/$/g, '');
  const rawSegments = cleanPath.split('/');
  const segments: string[] = [];

  let filterSearchParams: URLSearchParams | undefined;
  let searchParams: URLSearchParams | undefined;

  for (const segment of rawSegments) {
    if (isFilterSegment(segment)) {
      const [, params] = parseSegmentForSearchParams(segment);
      filterSearchParams = params;
    } else {
      const [baseSegment, params] = parseSegmentForSearchParams(segment);
      searchParams = params;
      segments.push(baseSegment);
    }
  }

  // Handle invalid or empty paths
  if (!segments.length) {
    return undefined;
  }

  let result: ParsedMilitaryPath = {
    type: AddressType.ADDRESS_TYPE_SUB_DIVISION,
    branch: undefined,
    baseName: '',
    acceptedSegments: [],
    discardedSegments: [],
    acceptedPathSegments: [],
  };
  const paginationParams = filterSearchParams || searchParams || new URLSearchParams();
  const sort: Sort = getSortFromURLSearchParams(paginationParams);
  const page: number = getPageFromURLSearchParams(paginationParams);

  for (let index = 0; index < segments.length; index++) {
    const segment = segments[index].toLowerCase();

    if (index === 0) {
      // Handle the first segment which should be:
      // 1. "<base-slug>-<military-branch>" for military base pages

      if (MILITARY_BRANCH_SUFFIXES.some(suffix => segment.endsWith(`-${suffix}`))) {
        const suffix = MILITARY_BRANCH_SUFFIXES.find(suffix => segment.endsWith(`-${suffix}`));
        const branch = MILITARY_BRANCH_SUFFIX_TO_BRANCH_MAP[suffix];
        const slug = segment.slice(0, -(`-${suffix}`).length).replace(/-/g, '_');

        if (slug.length === 0) { // Handle empty slug case
          return undefined;
        }

        result = {
          type: AddressType.ADDRESS_TYPE_SUB_DIVISION,
          branch,
          baseName: humanize(deDasherize(slug)),
          slug,
          acceptedSegments: [segment],
          acceptedPathSegments: [segment],
          discardedSegments: [],
          sort,
          page,
        };
      } else {
        return undefined;
      }

      const hasNextSegment = segments[index + 1] !== undefined;
      if (!hasNextSegment) {
        result.filter = filterSearchParams ? createFilterFromFilterParams(filterSearchParams) : undefined;
      }
    } else if (index === 1) {
      // Handle the second segment which can be a SEO filter or a filter
      const seoFilters = parseSeoFilter(segment);
      const isSeoFilterSegment = seoFilters !== undefined;

      if (isSeoFilterSegment) {
        // handle SEO filter case: "/naval-air-station-whiting-field-navy/<supported seo filter value>"
        //
        // If this segment is a SEO filter, we update the result with values related to the SEO filter,
        // discard the rest of the segments that wont be used.

        result.seoFilters = seoFilters;
        result.discardedSegments = segments.slice(2);
        result.acceptedSegments.push(segment);
        result.acceptedSeoFilterSegment = segment;
        result.filter = createFilterFromSeoFilter(seoFilters);
        break;
      }
      const discardedSegments = segments.slice(2);
      result.discardedSegments = discardedSegments;
      break;
    }
  }

  return result;
}

/**
 * Creates a strongly-typed ParsedMilitaryPath from a plain object
 * @param obj Object representing a ParsedMilitaryPath
 * @returns ParsedMilitaryPath object with proper typing
 */
export function parsedMilitaryPathFromJson(obj: Record<string, unknown>): ParsedMilitaryPath | undefined {
  if (!obj) return undefined;
  return {
    type: AddressType.ADDRESS_TYPE_SUB_DIVISION,
    branch: obj.branch as MilitaryBranchType,
    baseName: obj.baseName as string,
    acceptedSegments: Array.isArray(obj.acceptedSegments) ? obj.acceptedSegments.map(String) : undefined,
    discardedSegments: Array.isArray(obj.discardedSegments) ? obj.discardedSegments.map(String) : [],
    sort: obj.sort as Sort | undefined,
    slug: typeof obj.slug === 'string' ? obj.slug : undefined,
    filter: obj.filter as PartialDeep<Filter> | undefined,
    seoFilters: obj.seoFilters as SeoFilters | undefined,
  };
}
